import React, { useEffect, useState } from 'react'
import { Row, Col, Card, Form, Spinner, Alert } from 'react-bootstrap'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import * as FaIcons from 'react-icons/fa'
import { useSelector } from 'react-redux'

import SimpleMantenedorLayout from '../SimpleMantenedorLayout'
import { useMantenedores } from '../../context/MantenedoresContext'
import { useCierre } from '../../hooks/useCierre'
import CierreTabs from './CierreTabs'
import ResumenFinanciero from './ResumenFinanciero'
import DetalleRutas from './DetalleRutas'
import AnalisisDetallado from './AnalisisDetallado'

const CierreDiaADia = () => {
    const user = useSelector(state => state.auth.user)
    const { userPermissions } = useMantenedores()
    const permisos = userPermissions?.modules?.cierre || {}

    // Usar el hook useCierre para acceder a las funciones de resumen diario
    // autoFetch: false para evitar llamada innecesaria a getCierres
    const { 
        resumenDiario,
        loadingResumen,
        errorResumen,
        loadResumenDiario,
        searchCombinationsFromErrors
    } = useCierre(user, permisos, { autoFetch: false })

    // Obtener fecha actual
    const obtenerFechaActual = () => {
        return new Date()
    }

    // Estados principales
    const [fechaConsulta, setFechaConsulta] = useState(obtenerFechaActual())

    // Cargar datos iniciales
    useEffect(() => {
        if (permisos.canView) {
            const fechaFormateada = fechaConsulta.toISOString().split('T')[0]
            loadResumenDiario(fechaFormateada)
        }
    }, [permisos.canView, fechaConsulta])

    // Formatear fecha para mostrar (evita problemas de timezone)
    const formatearFechaDisplay = fecha => {
        const year = fecha.getFullYear()
        const month = String(fecha.getMonth() + 1).padStart(2, '0')
        const day = String(fecha.getDate()).padStart(2, '0')
        return `${day}-${month}-${year}`
    }

    // Formatear montos en CLP
    const formatearMonto = monto => {
        if (monto === null || monto === undefined || isNaN(monto)) {
            return '$0'
        }
        return new Intl.NumberFormat('es-CL', {
            style: 'currency',
            currency: 'CLP'
        }).format(Number(monto))
    }

    // Formatear porcentajes
    const formatearPorcentaje = porcentaje => {
        if (porcentaje === null || porcentaje === undefined || isNaN(porcentaje)) {
            return '0.00%'
        }
        return `${Number(porcentaje).toFixed(2)}%`
    }

    // Handler para cambio de fecha
    const handleFechaChange = fecha => {
        if (fecha) {
            setFechaConsulta(fecha)
        }
    }

    return (
        <SimpleMantenedorLayout
            mantenedorId="cierre"
            title="Resumen Día a Día"
            subtitle="Consulta resumen de operaciones por fecha"
            iconColor="#1976d2"
            breadcrumbs={[{ label: 'Cierre', path: '/mantenedores/cierre' }, { label: 'Resumen Diario' }]}
            showBackButton={false}>
            {/* Selector de Fecha */}
            <Row className="mt-1">
                <Col xs={12}>
                    <Card className="shadow-sm border-0">
                        <Card.Header className="bg-light border-0">
                            <h6 className="mb-0">
                                <FaIcons.FaCalendarAlt className="me-2" style={{ color: '#1976d2' }} />
                                Selección de Fecha
                            </h6>
                        </Card.Header>
                        <Card.Body>
                            <Row className="align-items-center">
                                <Col xs={12} md={4}>
                                    <Form.Group>
                                        <Form.Label style={{ fontWeight: '500', color: '#495057' }}>
                                            Fecha a Consultar
                                        </Form.Label>
                                        <DatePicker
                                            selected={fechaConsulta}
                                            onChange={handleFechaChange}
                                            dateFormat="dd/MM/yyyy"
                                            disabled={loadingResumen}
                                            className="form-control"
                                            placeholderText="Seleccione una fecha"
                                            showYearDropdown
                                            showMonthDropdown
                                            dropdownMode="select"
                                            maxDate={new Date()}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col xs={12} md={8} className="d-flex align-items-end">
                                    {loadingResumen && (
                                        <div className="d-flex align-items-center text-muted">
                                            <Spinner animation="border" size="sm" className="me-2" />
                                            Cargando resumen...
                                        </div>
                                    )}
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Error Alert */}
            {errorResumen && (
                <Row className="mt-3">
                    <Col xs={12}>
                        <Alert variant="danger">
                            <FaIcons.FaExclamationTriangle className="me-2" />
                            {errorResumen}
                        </Alert>
                    </Col>
                </Row>
            )}

            {/* Contenido Principal con Tabs */}
            {!loadingResumen && !errorResumen && resumenDiario && (
                <Row className="mt-4">
                    <Col xs={12}>
                        <CierreTabs
                            tabs={[
                                {
                                    eventKey: 'resumen',
                                    title: 'Resumen Financiero',
                                    component: (
                                        <ResumenFinanciero
                                            resumenDiario={resumenDiario}
                                            fechaConsulta={fechaConsulta}
                                            formatearMonto={formatearMonto}
                                            formatearPorcentaje={formatearPorcentaje}
                                            formatearFechaDisplay={formatearFechaDisplay}
                                        />
                                    )
                                },
                                {
                                    eventKey: 'rutas',
                                    title: 'Detalle de Rutas',
                                    component: (
                                        <DetalleRutas
                                            resumenDiario={resumenDiario}
                                            formatearMonto={formatearMonto}
                                            formatearPorcentaje={formatearPorcentaje}
                                        />
                                    )
                                },
                                {
                                    eventKey: 'analisis',
                                    title: 'Análisis Detallado',
                                    component: (
                                        <AnalisisDetallado
                                            resumenDiario={resumenDiario}
                                            formatearMonto={formatearMonto}
                                            formatearPorcentaje={formatearPorcentaje}
                                            user={user}
                                            permisos={permisos}
                                            searchCombinationsFromErrors={searchCombinationsFromErrors}
                                        />
                                    )
                                }
                            ]}
                            defaultActiveKey="resumen"
                        />
                    </Col>
                </Row>
            )}

            {/* Estado de carga inicial */}
            {loadingResumen && !resumenDiario && (
                <Row className="mt-5">
                    <Col xs={12}>
                        <div className="text-center p-5">
                            <Spinner animation="border" size="lg" style={{ color: '#1976d2' }} />
                            <div className="mt-3">
                                <h6 style={{ color: '#6c757d' }}>Cargando resumen diario...</h6>
                            </div>
                        </div>
                    </Col>
                </Row>
            )}
        </SimpleMantenedorLayout>
    )
}

export default CierreDiaADia
